import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Alert from 'components/shared/Alert';
import Modal from 'components/shared/Modal';
import NavigationTile from 'components/shared/NavigationTile';

import { renderModal } from 'utils/modal_util';
import { formatDateModified } from 'utils/format_date';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import slug from 'utils/slug';

import savedArrangements from 'domains/saved_arrangements';

import { prepareRestoreArrangement } from 'thunks/arrangement_thunks';

import EditArrangementModal from './EditArrangementModal';

import type { SavedArrangement } from 'domains/saved_arrangements';

type ManageArrangementsModalProps = {
  onClose: () => void;
};

function ManageArrangementsModal({ onClose }: ManageArrangementsModalProps) {
  const dispatch = useAppDispatch();
  const arrangements = useAppSelector(savedArrangements.selectors.selectAll);

  const [pendingArrangement, setPendingArrangement] = useState<SavedArrangement | null>(null);

  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);

  const [showEditDialog, setShowEditDialog] = useState(false);

  const pendingArrangementMenuId = useMemo(() => {
    if (!pendingArrangement) return '';

    return `${slug(pendingArrangement.name)}-${pendingArrangement.id}-navigation-tile`;
  }, [pendingArrangement]);

  function prepareDeleteArrangement(arrangement: SavedArrangement) {
    setPendingArrangement(arrangement);
    setShowConfirmDeleteDialog(true);
  }

  function prepareEditArrangement(arrangement: SavedArrangement) {
    dispatch(prepareRestoreArrangement(arrangement));
    setPendingArrangement(arrangement);
    setShowEditDialog(true);
  }

  function handleSelectArrangement(arrangement: SavedArrangement) {
    dispatch(prepareRestoreArrangement(arrangement));
  }

  function confirmDeleteArrangement() {
    if (!pendingArrangement) return;

    dispatch(savedArrangements.actions.destroy.pending(pendingArrangement.id));
    setPendingArrangement(null);
    setShowConfirmDeleteDialog(false);
  }

  function cancelEditArrangement() {
    setPendingArrangement(null);
    setShowEditDialog(false);
  }

  function cancelDeleteArrangement() {
    setPendingArrangement(null);
    setShowConfirmDeleteDialog(false);
  }

  const arrangementsTiles = arrangements.map((arrangement) => {
    const { preview_url: previewURL, name, id, updated_at: updated, canvases } = arrangement;

    const menuItems = [
      {
        label: 'Edit',
        icon: <FontAwesomeIcon icon="edit" />,
        click: () => prepareEditArrangement(arrangement),
      },
      {
        label: (
          <span>
            Delete <span className="screen-reader-text">{name}</span>
          </span>
        ),
        icon: <FontAwesomeIcon icon="trash-alt" />,
        click: () => prepareDeleteArrangement(arrangement),
      },
    ];

    return (
      <li key={arrangement.id}>
        <NavigationTile
          image={previewURL}
          imagePending={previewURL === ''}
          details={`${canvases.length} item${canvases.length > 1 ? 's' : ''}`}
          secondaryDetails={`Updated: ${formatDateModified(updated)}`}
          item={arrangement}
          menuItems={menuItems}
          name={name}
          clickHandler={() => handleSelectArrangement(arrangement)}
        />
      </li>
    );
  });

  return (
    <React.Fragment>
      <Modal
        title="Manage Arrangements"
        labelledBy="manage-arrangements-modal"
        modalClass={['manage-arrangements-modal']}
        lastFocusedElementId="content-menu-trigger-arrangements"
        onClose={onClose}
      >
        <ul className="modal-list-items">{arrangementsTiles}</ul>
      </Modal>

      {showConfirmDeleteDialog &&
        renderModal(
          <Alert
            labelledBy="confirm-delete-arrangement-dialog"
            lastFocusedElementId={pendingArrangementMenuId}
            title="Confirm Delete"
            message="Are you sure you want to delete this saved arrangement?"
            onClose={cancelDeleteArrangement}
            onCancel={cancelDeleteArrangement}
            onContinue={confirmDeleteArrangement}
          />
        )}

      {showEditDialog &&
        pendingArrangement &&
        renderModal(
          <EditArrangementModal onClose={cancelEditArrangement} arrangement={pendingArrangement} />
        )}
    </React.Fragment>
  );
}

export default ManageArrangementsModal;
